import { Reveal, SectionHeader } from "@/components/Reveal";
import FounderFrame from "@/components/FounderFrame";
import GearMark from "@/components/GearMark";
import { CTABand, PrimaryCTA } from "@/components/CTA";
import { CONTACT_EMAIL } from "@/config";
import usePageMeta from "@/hooks/usePageMeta";

const VALUES = [
  ["01", "Precision", "Numbers that reconcile. Briefs that hold up in front of a customer."],
  ["02", "Autonomy", "Software that acts; people who approve. The workflow runs so the team can sell."],
  ["03", "Trust", "Partner-grade permissions and a full audit trail, by default."],
];

export default function About() {
  usePageMeta(
    "About — Transactr",
    "Transactr builds the autonomous layer for AWS Marketplace revenue — for channel partners, ISVs, distributors, and brokers."
  );
  return (
    <>
      <section className="relative overflow-hidden">
        <div className="blueprint-grid pointer-events-none absolute inset-0 opacity-60" />
        <div className="container-t relative pb-16 pt-36 lg:pb-24 lg:pt-44">
          <SectionHeader
            eyebrow="About"
            title={
              <>
                The autonomous layer for{" "}
                <em className="font-accent font-medium italic text-vermillion">marketplace revenue</em>.
              </>
            }
          />
          <div className="mt-10 grid gap-10 lg:grid-cols-2">
            <Reveal delay={0.1}>
              <p className="text-lg leading-relaxed text-inksoft">
                Marketplace revenue is won in the details — renewal dates, partner terms, co-sell
                status, the offer that should have gone out last week. Those details live in too many
                places, and the people accountable for them spend their weeks reconciling instead of
                selling.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="text-lg leading-relaxed text-inksoft">
                Transactr builds the layer that reads all of it and acts on what matters: AI-native
                workflows that turn AWS Marketplace data into upsell motion for channel partner sales
                teams and alliances leaders at ISVs.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="container-t pb-24 lg:pb-32" data-testid="about-values-section">
        <div className="grid gap-5 lg:grid-cols-3">
          {VALUES.map(([i, t, d], idx) => (
            <Reveal key={i} delay={idx * 0.08} className="h-full">
              <div className="group h-full rounded-3xl border border-line bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-40px_rgba(31,28,25,0.35)] lg:p-10">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[11px] text-vermillion">{i}</span>
                  <GearMark size={26} className="opacity-25 transition-all duration-700 group-hover:rotate-180 group-hover:opacity-100" />
                </div>
                <h3 className="mt-10 font-display text-xl font-bold tracking-tight text-ink lg:text-2xl">{t}</h3>
                <p className="mt-3 leading-relaxed text-inksoft">{d}</p>
                <div className="mt-7 h-px w-12 bg-bluerule transition-all duration-500 group-hover:w-24 group-hover:bg-vermillion" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FOUNDER */}
      <section className="bg-parchment/60 py-24 lg:py-32" data-testid="about-founder-section">
        <div className="container-t grid items-center gap-14 lg:grid-cols-[0.85fr_1.15fr]">
          <Reveal>
            <FounderFrame />
          </Reveal>
          <div>
            <Reveal>
              <p className="eyebrow">
                <span className="eyebrow-mark" />
                The founder
              </p>
              <h2 className="mt-5 font-display text-3xl font-extrabold tracking-[-0.02em] text-ink lg:text-4xl">
                Gary
              </h2>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-inksoft">
                Founder · {`gary@trxtr.io`}
              </p>
              <blockquote className="mt-8 border-l-2 border-vermillion pl-6 font-accent text-2xl italic leading-snug text-ink lg:text-[1.7rem]">
                "Partners shouldn't have to dig for revenue that's already in their data. We build the
                tools that surface it — automatically."
              </blockquote>
              <p className="mt-8 max-w-xl leading-relaxed text-inksoft">
                Gary runs Transactr — and every demo and consultation happens directly with him. No
                gatekeeping, no handoff: you bring the use case, he brings the product.
              </p>
              <div className="mt-9">
                <PrimaryCTA testid="about-book-consultation-btn">Book a consultation</PrimaryCTA>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
